import { useState, type RefObject } from 'react';
import { DockPanel, type DockPanelState } from '@/common/components/DockPanel';
import { useSession } from '@/features/auth/hooks/useSession';
import { useActivitiesTab } from '@/features/activities/useActivities';
import { useEmailApprovals, isEmailReply } from '@/features/activities/useEmailApprovals';
import { EmailDraftReviewCard } from '@/features/activities/EmailDraftReviewCard';
import {
  useActionApprovals,
  useDecideActionApproval,
} from '@/common/hooks/useActionApprovals';
import { ActionApprovalCard } from '@/common/blocks/ActionApprovalCard';
import { useOpenActivityItem } from '@/features/activities/useOpenActivityItem';
import { usePastApprovals, type PastApproval } from '@/features/activities/usePastApprovals';
import type { ActionOperation } from '@/common/utils/action-vocabulary';
import { resolveWorkspaceId } from '@/lib/workspace-routing';
import './ActivityCenter.css';

const TABS = [
  { id: 'review', label: 'Review' },
  { id: 'past', label: 'Past' },
] as const;
type TabId = (typeof TABS)[number]['id'];

const EMPTY_LABEL: Record<TabId, string> = {
  review: 'Nothing needs your attention.',
  past: 'Nothing here yet.',
};

/** Compact glyph for a past-row, keyed by the action's coarse operation. */
const PAST_ICON: Record<ActionOperation, string> = {
  send: '↗',
  create: '＋',
  update: '✎',
  delete: '✕',
  other: '•',
};

function formatPastTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Error state for a tab whose feed failed to load — with a retry, so a transient
 *  network/500 blip doesn't masquerade as "nothing here". Mirrors the module
 *  error+retry pattern (#5297). */
function ActivityError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="activity-error" role="alert">
      <span>Couldn’t load this. Check your connection and try again.</span>
      <button type="button" className="activity-retry-btn" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

/** One decided-approval history row: outcome + what it acted on + when. Read-only. */
function PastApprovalRow({ item }: { item: PastApproval }) {
  return (
    <li>
      <div className="activity-row activity-row--past">
        <span
          className={`activity-past-icon activity-past-icon--${item.operation}`}
          aria-hidden="true"
        >
          {PAST_ICON[item.operation]}
        </span>
        <span className="activity-past-body">
          <span className="activity-row-main">
            <span className="activity-past-outcome">{item.label}</span>
            {item.title ? <span className="activity-past-title"> — {item.title}</span> : null}
          </span>
          <span className="activity-row-sub">{formatPastTime(item.decidedAt)}</span>
        </span>
      </div>
    </li>
  );
}

/**
 * Notification / Activity Center — the default panel the header bell opens.
 * Review/Past tabs over the existing `/activities` feed. Rendered in the
 * shared DockPanel so it rides the bell flight + docked-pane placement. Accepts
 * the same panel props as DailySummaryModal so it drops into MainLayout's mounts.
 * Rows route to the thing they're about (object's module / conversation) via
 * useOpenActivityItem — the same navigation the rest of Xavior uses.
 */
export function ActivityCenter({
  onClose,
  isOpen = true,
  mode = 'modal',
  dockState,
  cardRef,
  landingReveal = false,
  onLandingRevealComplete,
}: {
  onClose: () => void;
  isOpen?: boolean;
  mode?: 'modal' | 'panel';
  dockState?: DockPanelState;
  cardRef?: RefObject<HTMLDivElement | null>;
  landingReveal?: boolean;
  onLandingRevealComplete?: () => void;
}) {
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const [tab, setTab] = useState<TabId>('review');
  const enabled = Boolean(token && workspaceId);
  const { items, reviewCount, isLoading, error, refetch } = useActivitiesTab(
    workspaceId,
    tab,
    enabled,
  );
  // Drafted email replies awaiting sign-off land in the Review tab (Phase 4).
  const { items: emailDrafts } = useEmailApprovals();
  // Every pending action the email card does NOT claim (cross-object create/edit/delete on
  // event/task/note/contact, and any future object.*/send that isn't an email reply) lands in
  // Review as a generic action card — so no pending approval is ever rendered nowhere. The email
  // replies (email.send / send_email) are the ONLY ones the specialized email card owns; we exclude
  // exactly those here rather than allowlisting create|update|delete (Automations cross-object
  // plan, design §5.3).
  const { items: actionApprovals } = useActionApprovals(token, workspaceId);
  const crossObjectActions = actionApprovals.filter((a) => !isEmailReply(a));
  const decideAction = useDecideActionApproval(token, workspaceId);
  const { openItem, canOpen } = useOpenActivityItem();
  // Past tab = decided-approval history (Automations v2 §5). Fetch only while Past is open.
  const past = usePastApprovals(tab === 'past');

  // Email drafts + cross-object actions are all "review" work — fold them into the count.
  const reviewBadge = reviewCount + emailDrafts.length + crossObjectActions.length;
  const showReviewCards =
    tab === 'review' && (emailDrafts.length > 0 || crossObjectActions.length > 0);

  return (
    <DockPanel
      title="Notifications"
      ariaLabel="Notifications"
      mode={mode}
      isOpen={isOpen}
      dockState={dockState}
      cardRef={cardRef}
      landingReveal={landingReveal}
      onLandingRevealComplete={onLandingRevealComplete}
      onClose={onClose}
    >
      <div className="db-settings-tabs activity-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`db-settings-tab${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === 'review' && reviewBadge > 0 ? ` (${reviewBadge})` : ''}
          </button>
        ))}
      </div>

      {showReviewCards && (
        <div className="email-draft-review-list">
          {decideAction.isError && (
            <p className="activity-decide-error" role="alert">
              That decision didn’t save — check your connection and try again.
            </p>
          )}
          {emailDrafts.map((draft) => (
            <EmailDraftReviewCard key={draft.id} approval={draft} />
          ))}
          {crossObjectActions.map((action) => (
            <ActionApprovalCard
              key={action.id}
              approval={action}
              busy={decideAction.isPending}
              onDecide={(vars, opts) => decideAction.mutate(vars, opts)}
            />
          ))}
        </div>
      )}

      {tab === 'past' ? (
        past.isLoading ? (
          <p className="activity-empty">Loading…</p>
        ) : past.error && past.items.length === 0 ? (
          // A failed history fetch must NOT masquerade as "nothing here yet" — but
          // if a background refetch failed while we still hold rows, keep showing
          // them (react-query retains prior data) rather than blanking to an error.
          <ActivityError onRetry={() => past.refetch()} />
        ) : past.items.length === 0 ? (
          <p className="activity-empty">{EMPTY_LABEL.past}</p>
        ) : (
          <ul className="activity-list">
            {past.items.map((item) => (
              <PastApprovalRow key={item.id} item={item} />
            ))}
          </ul>
        )
      ) : isLoading ? (
        <p className="activity-empty">Loading…</p>
      ) : error && items.length === 0 ? (
        // Feed error with no rows to show → error+Retry (never a misleading empty).
        // If a background refetch failed but we still hold rows, fall through and keep
        // the stale rows. Review cards are separate data — if present the panel already
        // has actionable content, so suppress the feed error the way we suppress empty.
        showReviewCards ? null : <ActivityError onRetry={() => refetch()} />
      ) : items.length === 0 ? (
        // Review cards alone still count as "something to review" — suppress the empty state.
        showReviewCards ? null : <p className="activity-empty">{EMPTY_LABEL[tab]}</p>
      ) : (
        <ul className="activity-list">
          {items.map((item) => {
            const main = item.title || item.description || 'Activity';
            const sub = item.title && item.description ? item.description : null;
            const body = (
              <>
                <span className="activity-row-main">{main}</span>
                {sub ? <span className="activity-row-sub">{sub}</span> : null}
              </>
            );
            return (
              <li key={item.id}>
                {canOpen(item) ? (
                  <button
                    type="button"
                    className="activity-row activity-row--clickable"
                    onClick={() => {
                      openItem(item);
                      onClose();
                    }}
                  >
                    {body}
                  </button>
                ) : (
                  <div className="activity-row">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </DockPanel>
  );
}
